import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import { Upload, FileText, Sparkles, Loader2, CheckCircle2, ArrowLeft, ScanLine, Coins, Shield, CreditCard, Users, Calendar, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const LENS_CONTEXTS = {
  // Lumina contexts
  'upload-patient-chart': {
    title: 'Upload Patient Chart via Lens',
    subtitle: 'Import records from your existing EHR — create or enrich a patient chart instantly',
    docs: [
      { id: 'ccda', label: 'CCDA / CCD Export', icon: FileText, desc: 'Upload a Continuity of Care Document from your EHR', color: 'bg-violet-50 text-violet-600' },
      { id: 'demographics', label: 'Patient Demographics (Excel)', icon: ClipboardList, desc: 'Upload an Excel/CSV with patient names, DOB, insurance', color: 'bg-blue-50 text-blue-600' },
      { id: 'encounter', label: 'Encounter Note / Visit Summary', icon: FileText, desc: 'Scan or upload a visit note from an external EHR', color: 'bg-emerald-50 text-emerald-600' },
    ],
    results: {
      ccda: { type: 'CCDA Import', fields: [
        { label: 'Patient', value: 'John Smith', confidence: 99 },
        { label: 'DOB', value: '03/15/1965', confidence: 98 },
        { label: 'Active Problems', value: 'E11.9 Type 2 DM, I10 Hypertension, E78.5 Hyperlipidemia', confidence: 96 },
        { label: 'Medications', value: '3 active medications extracted', confidence: 95 },
        { label: 'Allergies', value: 'Penicillin (Rash), Sulfa (Hives)', confidence: 97 },
        { label: 'Insurance', value: 'Blue Cross Blue Shield PPO', confidence: 98 },
      ], actions: ['Create New Patient Chart', 'Merge into Existing Chart'] },
      demographics: { type: 'Patient Demographics', fields: [
        { label: 'Patients Found', value: '12 patient records detected', confidence: 96 },
        { label: 'Fields Mapped', value: 'Name, DOB, SSN, Address, Phone, Insurance, PCP', confidence: 94 },
        { label: 'Duplicates', value: '2 potential matches with existing patients', confidence: 91 },
        { label: 'Insurance Coverage', value: '10 with active coverage, 2 need verification', confidence: 93 },
      ], actions: ['Import All Patients', 'Review & Import Individually'] },
      encounter: { type: 'Encounter Note', fields: [
        { label: 'Patient', value: 'Maria Garcia', confidence: 97 },
        { label: 'Visit Date', value: '03/10/2026', confidence: 99 },
        { label: 'Chief Complaint', value: 'Follow-up for diabetes management', confidence: 94 },
        { label: 'Assessment', value: 'E11.9 — A1c 7.2%, up from 6.8%', confidence: 93 },
        { label: 'Plan', value: 'Increase Metformin to 1000mg BID, recheck A1c in 3 months', confidence: 92 },
        { label: 'CPT Suggested', value: '99214 — Established, Moderate Complexity', confidence: 90 },
      ], actions: ['Create Chart from Note', 'Add to Existing Patient'] },
    },
  },
  'upload-encounter-lens': {
    title: 'Upload Clinical Documents via Lens',
    subtitle: 'Scan referrals, lab reports, or visit notes from other providers',
    docs: [
      { id: 'referral', label: 'Referral Letter', icon: FileText, desc: 'Incoming referral from another provider', color: 'bg-violet-50 text-violet-600' },
      { id: 'lab', label: 'Lab Report', icon: ClipboardList, desc: 'Blood work, pathology, or imaging results', color: 'bg-red-50 text-red-600' },
      { id: 'discharge', label: 'Hospital Discharge Summary', icon: FileText, desc: 'Post-hospitalization discharge document', color: 'bg-amber-50 text-amber-600' },
    ],
    results: {
      referral: { type: 'Referral', fields: [
        { label: 'Referring Provider', value: 'Dr. Michael Park, MD — Cardiology', confidence: 97 },
        { label: 'Patient', value: 'John Smith', confidence: 99 },
        { label: 'Reason', value: 'Co-management of Type 2 DM with cardiac clearance', confidence: 95 },
        { label: 'Echo Results', value: 'EF 60%, Grade 1 diastolic dysfunction', confidence: 93 },
        { label: 'Recommendation', value: 'Optimize glycemic control, cardiology f/u 12 months', confidence: 92 },
      ], actions: ['Add to Patient Chart', 'Schedule Follow-Up'] },
      lab: { type: 'Lab Report', fields: [
        { label: 'Patient', value: 'John Smith', confidence: 99 },
        { label: 'HbA1c', value: '7.2% (H) — Target: < 7.0%', confidence: 97 },
        { label: 'Fasting Glucose', value: '142 mg/dL (H) — Ref: 70-100', confidence: 96 },
        { label: 'LDL', value: '128 mg/dL (H) — Ref: < 100', confidence: 95 },
        { label: 'eGFR', value: '72 mL/min (L) — Ref: > 90', confidence: 94 },
      ], actions: ['Add Labs to Chart', 'Flag Abnormals for Review'] },
      discharge: { type: 'Discharge Summary', fields: [
        { label: 'Patient', value: 'John Smith', confidence: 98 },
        { label: 'Admission', value: '12/20/2025 — Diabetic Ketoacidosis', confidence: 96 },
        { label: 'New Medications', value: 'Insulin Glargine 20u QHS (NEW)', confidence: 95 },
        { label: 'Follow-Up', value: 'PCP within 7 days, Endo referral', confidence: 94 },
      ], actions: ['Update Chart & Meds', 'Schedule Follow-Up Visit'] },
    },
  },
  // revQ contexts
  'scan-insurance': {
    title: 'Scan Insurance Card via Lens',
    subtitle: 'Upload or photograph insurance cards for instant eligibility data extraction',
    docs: [
      { id: 'insurance_front', label: 'Insurance Card (Front)', icon: Shield, desc: 'Plan name, member ID, group number', color: 'bg-emerald-50 text-emerald-600' },
      { id: 'insurance_back', label: 'Insurance Card (Back)', icon: Shield, desc: 'Claims address, phone, authorization info', color: 'bg-blue-50 text-blue-600' },
    ],
    results: {
      insurance_front: { type: 'Insurance Card (Front)', fields: [
        { label: 'Plan', value: 'Blue Cross Blue Shield PPO', confidence: 99 },
        { label: 'Member ID', value: 'BCB-987654321', confidence: 98 },
        { label: 'Group #', value: 'GRP-5432', confidence: 97 },
        { label: 'Copay', value: '$30 Office / $50 Specialist', confidence: 94 },
        { label: 'Effective', value: '01/01/2026', confidence: 96 },
      ], actions: ['Update Patient Insurance', 'Run Eligibility Check'] },
      insurance_back: { type: 'Insurance Card (Back)', fields: [
        { label: 'Claims Address', value: 'PO Box 14079, Lexington, KY 40512', confidence: 96 },
        { label: 'Customer Service', value: '(800) 123-4567', confidence: 99 },
        { label: 'Pre-Auth Phone', value: '(800) 987-6543', confidence: 97 },
        { label: 'EDI Payer ID', value: 'BCBS-00234', confidence: 95 },
      ], actions: ['Save Payer Info', 'Link to Clearinghouse'] },
    },
  },
  'upload-superbill': {
    title: 'Upload Superbill via Lens',
    subtitle: 'Scan superbills or encounter forms from your external EHR for automatic charge capture',
    docs: [
      { id: 'superbill', label: 'Superbill / Encounter Form', icon: CreditCard, desc: 'Paper or PDF superbill with CPT/ICD codes', color: 'bg-emerald-50 text-emerald-600' },
      { id: 'fee_sheet', label: 'Fee Schedule / Charge Sheet', icon: ClipboardList, desc: 'Batch charges from another system', color: 'bg-amber-50 text-amber-600' },
    ],
    results: {
      superbill: { type: 'Superbill', fields: [
        { label: 'Patient', value: 'Sarah Johnson', confidence: 98 },
        { label: 'DOS', value: '03/15/2026', confidence: 99 },
        { label: 'Provider', value: 'Dr. Emily Chen, NPI: 1234567890', confidence: 97 },
        { label: 'CPT Codes', value: '99214 (Est. Visit), 93000 (EKG), 36415 (Venipuncture)', confidence: 95 },
        { label: 'ICD-10', value: 'E11.9, I10, E78.5', confidence: 94 },
        { label: 'Total Charges', value: '$380.00', confidence: 96 },
      ], actions: ['Create Claim from Superbill', 'Queue for AI Scrub'] },
      fee_sheet: { type: 'Fee Schedule', fields: [
        { label: 'Encounters', value: '8 encounters detected for 03/15/2026', confidence: 94 },
        { label: 'Total Charges', value: '$2,840.00 across 8 patients', confidence: 93 },
        { label: 'CPT Codes', value: '15 unique CPT codes extracted', confidence: 92 },
        { label: 'Missing Data', value: '1 encounter missing ICD-10 code', confidence: 90 },
      ], actions: ['Import All Charges', 'Review & Import Individually'] },
    },
  },
  'upload-eob': {
    title: 'Upload EOB / ERA via Lens',
    subtitle: 'Scan Explanation of Benefits or paper remittances for automatic payment posting',
    docs: [
      { id: 'eob', label: 'EOB (Explanation of Benefits)', icon: FileText, desc: 'Paper EOB from payer', color: 'bg-blue-50 text-blue-600' },
      { id: 'check', label: 'Insurance Check / Remittance', icon: CreditCard, desc: 'Payment check with remittance advice', color: 'bg-emerald-50 text-emerald-600' },
    ],
    results: {
      eob: { type: 'EOB', fields: [
        { label: 'Payer', value: 'Aetna PPO', confidence: 98 },
        { label: 'Check #', value: '2026-88421', confidence: 97 },
        { label: 'Claims', value: '3 claim lines processed', confidence: 95 },
        { label: 'Billed', value: '$1,240.00', confidence: 96 },
        { label: 'Allowed', value: '$980.00', confidence: 94 },
        { label: 'Paid', value: '$784.00 (80% of allowed)', confidence: 95 },
        { label: 'Patient Resp.', value: '$196.00 (copay + coinsurance)', confidence: 93 },
      ], actions: ['Auto-Post Payments', 'Review Before Posting'] },
      check: { type: 'Remittance', fields: [
        { label: 'Payer', value: 'UnitedHealth Group', confidence: 97 },
        { label: 'Amount', value: '$1,560.00', confidence: 99 },
        { label: 'Claims', value: '5 claims referenced', confidence: 94 },
        { label: 'Adjustments', value: '$420.00 contractual', confidence: 93 },
      ], actions: ['Match & Post Payments', 'Reconcile with Bank'] },
    },
  },
  // Echo contexts
  'upload-schedule': {
    title: 'Get started in 30 seconds — scan your schedule',
    subtitle: 'Import appointment lists from your external EHR to prep for the day ahead',
    docs: [
      { id: 'schedule_list', label: 'Appointment List / Schedule', icon: Calendar, desc: 'Daily or weekly schedule from external EHR', color: 'bg-blue-50 text-blue-600' },
      { id: 'patient_list', label: 'Patient Roster (Excel/CSV)', icon: Users, desc: 'Bulk import patient demographics', color: 'bg-violet-50 text-violet-600' },
    ],
    results: {
      schedule_list: { type: 'Schedule Import', fields: [
        { label: 'Date', value: 'March 18, 2026', confidence: 99 },
        { label: 'Appointments', value: '24 appointments extracted', confidence: 96 },
        { label: 'Providers', value: 'Dr. Chen (12), Dr. Wilson (8), Dr. Mitchell (4)', confidence: 95 },
        { label: 'New Patients', value: '3 new patient visits', confidence: 94 },
        { label: 'Conflicts', value: '1 potential double-booking at 10:30 AM', confidence: 91 },
      ], actions: ['Import to Calendar', 'Review Conflicts First'] },
      patient_list: { type: 'Patient Roster', fields: [
        { label: 'Patients Found', value: '45 patient records', confidence: 96 },
        { label: 'Fields', value: 'Name, DOB, Phone, Insurance, PCP', confidence: 94 },
        { label: 'New Patients', value: '12 not yet in system', confidence: 93 },
        { label: 'Existing Matches', value: '33 matched to current patients', confidence: 95 },
      ], actions: ['Import All', 'Import New Only'] },
    },
  },
  'scan-checkin': {
    title: 'Scan insurance card at check-in',
    subtitle: 'Photograph insurance cards and IDs for instant patient verification',
    docs: [
      { id: 'id_card', label: 'Patient ID / Drivers License', icon: Users, desc: 'Verify identity, extract address', color: 'bg-blue-50 text-blue-600' },
      { id: 'ins_card', label: 'Insurance Card', icon: Shield, desc: 'Verify coverage, extract member info', color: 'bg-emerald-50 text-emerald-600' },
    ],
    results: {
      id_card: { type: 'Patient ID', fields: [
        { label: 'Name', value: 'Amanda L. Rodriguez', confidence: 99 },
        { label: 'DOB', value: '05/14/1988', confidence: 98 },
        { label: 'Address', value: '456 Oak Ave, Springfield, IL 62702', confidence: 95 },
        { label: 'ID Match', value: 'Matches patient record P-2026-042', confidence: 97 },
      ], actions: ['Confirm Identity', 'Update Address'] },
      ins_card: { type: 'Insurance Card', fields: [
        { label: 'Plan', value: 'Blue Cross PPO', confidence: 99 },
        { label: 'Member ID', value: 'BCB-987654321', confidence: 98 },
        { label: 'Copay', value: '$30 Office Visit', confidence: 94 },
        { label: 'Status', value: 'Active — verified today', confidence: 96 },
      ], actions: ['Collect $30 Copay', 'Update Insurance Info'] },
    },
  },
};

const LensWorkflowDemo = ({ open, onOpenChange, context = 'upload-patient-chart' }) => {
  const { isDemo } = useAppMode();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const ctx = LENS_CONTEXTS[context] || LENS_CONTEXTS['upload-patient-chart'];

  const handleDocSelect = (docId) => {
    setSelectedDoc(docId);
    setProcessing(true);
    setTimeout(() => {
      setResult(ctx.results[docId]);
      setProcessing(false);
    }, 1800);
  };

  const handleClose = () => { setSelectedDoc(null); setResult(null); setProcessing(false); onOpenChange(false); };
  const handleReset = () => { setSelectedDoc(null); setResult(null); setProcessing(false); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lens-workflow-demo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ScanLine className="h-5 w-5 text-violet-600" />
            {ctx.title}
          </DialogTitle>
          <DialogDescription className="text-xs">{ctx.subtitle}</DialogDescription>
        </DialogHeader>

        {/* Bridge message */}
        <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 flex items-start gap-2">
          <Sparkles className="h-4 w-4 text-violet-600 mt-0.5 shrink-0" />
          <p className="text-xs text-violet-700">
            <strong>Works with any EHR.</strong> Simply upload, photograph, or drag-and-drop documents from your current system. AriaOne Lens extracts structured data instantly — no integration required.
          </p>
        </div>

        {!selectedDoc && !processing && !result && (
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Select document type to try</p>
            <div className="grid grid-cols-1 gap-2">
              {ctx.docs.map((doc) => {
                const Icon = doc.icon;
                return (
                  <button key={doc.id} onClick={() => handleDocSelect(doc.id)} className="flex items-center gap-3 p-3 rounded-xl border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all text-left" data-testid={`lens-demo-${doc.id}`}>
                    <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0", doc.color.split(' ')[0])}>
                      <Icon className={cn("h-5 w-5", doc.color.split(' ')[1])} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{doc.label}</p>
                      <p className="text-xs text-muted-foreground">{doc.desc}</p>
                    </div>
                    <Badge variant="outline" className="text-[9px] ml-auto shrink-0 gap-1"><Coins className="h-3 w-3 text-amber-500" /> 1 credit</Badge>
                  </button>
                );
              })}
            </div>

            {/* Upload area */}
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:border-violet-400 hover:bg-violet-50/30 transition-all">
                <Upload className="h-5 w-5 text-slate-400 mx-auto mb-1" />
                <p className="text-xs font-medium text-slate-600">Or upload your own document</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">PDF, PNG, JPG, Excel, CSV</p>
              </div>
              <input type="file" className="hidden" accept="image/*,.pdf,.xlsx,.csv" onChange={() => handleDocSelect(ctx.docs[0].id)} />
            </label>
          </div>
        )}

        {processing && (
          <div className="py-10 text-center">
            <Loader2 className="h-8 w-8 text-violet-500 mx-auto animate-spin mb-3" />
            <p className="font-medium text-slate-900 text-sm">AriaOne Lens Processing...</p>
            <p className="text-xs text-slate-500 mt-1">AI is extracting structured data from your document</p>
          </div>
        )}

        {result && !processing && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Badge className="bg-emerald-50 text-emerald-700 text-xs gap-1"><CheckCircle2 className="h-3 w-3" /> {result.type} — {result.fields.length} fields extracted</Badge>
              <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={handleReset} data-testid="lens-demo-try-another">
                <ArrowLeft className="h-3 w-3" /> Try Another
              </Button>
            </div>

            <div className="space-y-1.5" data-testid="lens-demo-results">
              {result.fields.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{f.label}</p>
                    <p className={cn("text-xs font-medium", f.value.includes('(H)') || f.value.includes('(NEW)') ? 'text-red-600' : f.value.includes('(L)') ? 'text-amber-600' : 'text-slate-800')}>{f.value}</p>
                  </div>
                  <Badge variant="outline" className={cn("text-[9px]", f.confidence >= 95 ? 'border-emerald-300 text-emerald-600' : f.confidence >= 90 ? 'border-amber-300 text-amber-600' : 'border-red-300 text-red-600')}>{f.confidence}%</Badge>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-1">
              {result.actions.map((action, i) => (
                <Button key={i} className={i === 0 ? "bg-slate-900 hover:bg-slate-800 flex-1 text-xs" : "flex-1 text-xs"} variant={i === 0 ? "default" : "outline"} onClick={() => { toast.success(action, { description: 'Data processed from Lens extraction' }); handleClose(); }} data-testid={`lens-action-${i}`}>
                  {action}
                </Button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LensWorkflowDemo;
